import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { Segmented } from "@/components/controls/Segmented";

import { FREE_FEATURES as FREE, TEAM_FEATURES as TEAM } from "@/lib/plans";
const INTERVALS = [{ value: "month", label: "Monthly" }, { value: "year", label: "Yearly" }];

const Feature = ({ text }) => (
  <li className="flex items-start gap-2 font-semibold"><Check size={20} className="shrink-0 text-[var(--accent)]" />{text}</li>
);

export default function PricingPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [interval, setPeriod] = useState("year");
  const [busy, setBusy] = useState(false);
  const access = user?.access;

  const subscribe = async () => {
    if (!user) return navigate("/login");
    setBusy(true);
    try {
      const { checkout_url } = await api.checkout(interval === "year" ? "team_yearly" : "team_monthly");
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };
  const cancel = async () => {
    try { toast.success((await api.cancelPlan()).message); refresh(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <PageShell>
      <AppHeader />
      <div className="pt-2 space-y-2">
        <p className="ctl-label">Plans</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">Pick your plan</h1>
        <p className="font-medium text-[var(--muted)]">Every new coach gets a 14-day free trial with all Team features. No card needed.</p>
      </div>
      <Segmented options={INTERVALS} value={interval} onChange={setPeriod} testPrefix="pricing-interval" />
      <div className="grid gap-4 md:grid-cols-2 max-w-4xl">
        <div className="card space-y-4 !p-6" data-testid="plan-free-card">
          <h2 className="font-heading text-3xl font-extrabold uppercase">Free</h2>
          <p className="font-heading text-5xl font-extrabold">$0</p>
          <ul className="space-y-2">{FREE.map((f) => <Feature key={f} text={f} />)}</ul>
          <p className="text-sm font-medium text-[var(--muted)]">No saving and no team sharing.</p>
        </div>
        <div className="card space-y-4 !p-6 plan-team" data-testid="plan-team-card">
          <h2 className="font-heading text-3xl font-extrabold uppercase">Team</h2>
          <p className="font-heading text-5xl font-extrabold" data-testid="plan-team-price">
            {interval === "year" ? "$79" : "$9.99"}<span className="text-xl text-[var(--muted)]">/{interval === "year" ? "year" : "month"}</span>
          </p>
          {interval === "year" && <p className="text-sm font-bold text-[var(--accent-strong)]">Save 34% vs monthly</p>}
          <ul className="space-y-2">{TEAM.map((f) => <Feature key={f} text={f} />)}</ul>
          {user === null ? (
            <div className="btn-ghost w-full opacity-60" data-testid="subscribe-loading">Loading...</div>
          ) : access?.plan === "team" ? (
            <div className="space-y-2">
              <p className="font-bold" data-testid="plan-active-text">You're on the Team plan ({access.interval === "year" ? "yearly" : "monthly"}).</p>
              <button className="btn-ghost w-full" data-testid="cancel-plan-button" onClick={cancel}>Cancel at period end</button>
            </div>
          ) : (
            <button className="btn-primary w-full" data-testid="subscribe-button" onClick={subscribe} disabled={busy}>
              {busy && <Loader2 size={18} className="animate-spin" />} {user ? "Subscribe" : "Log in to subscribe"}
            </button>
          )}
          {access?.plan === "trial" && <p className="text-sm font-semibold" data-testid="trial-days-left">{access.trial_days_left} trial days left.</p>}
        </div>
      </div>
    </PageShell>
  );
}
